import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { playRouteForMapPoint } from "@/features/map/mapPointRoutes"
import {
  getAirshipActiveMissions,
  getBaseHexes,
  getBaseMap,
  getMyBase,
} from "@/api/gameApi"
import GuardiansMapFiltersPanel, {
  MapFiltersToggleButton,
} from "@/features/map/GuardiansMapFiltersPanel"
import MapBaseDetailSheet from "@/features/map/MapBaseDetailSheet"
import {
  countActiveMapFilters,
  DEFAULT_GUARDIANS_MAP_FILTERS,
  filterWorldMapPoints,
  GuardiansMapFilters,
} from "@/features/map/guardiansMapFilters"
import { useGuardiansWorldMapPoints } from "@/features/map/useGuardiansWorldMapPoints"
import { citiesToMapPolygons } from "@/features/map/mapCityPolygons"
import { LOCATION_SCAN_MAP_HTML } from "@/map/locationScanMapHtml"
import { getPlayerGpsOnce, watchPlayerGps, PlayerGps } from "@/utils/playerGps"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { LocationPoint, WorldMapPointKind } from "@/types/location"
import { BaseMapMarker, MapPlane } from "@/types/base"
import {
  buildMapIconsPayload,
  loadMapMarkerIcons,
  MapMarkerIconSet,
  resolveMapMarkerIconRef,
  resolveMapMarkerIconUri,
  resolvePlayerMapIconUri,
} from "@/utils/mapMarkerIcons"
import {
  connectLocationSocket,
  disconnectLocationSocket,
  emitLocationEventWhenReady,
  normalizeLocationMapUpdatedPlayer,
  onLocationEvent,
} from "@/socket/locationSocket"
import { expandMapPlayersToTrailMarkers } from "@/utils/mapPlayers"
import { MapPlayerPresence } from "@/types/location"
import { lerpAirshipMissionPos } from "@/utils/airship"
import { WorldMapPoint, worldMapPointsToLocationPoints } from "@/utils/worldMapPoints"
import {
  listActionableWorldMapPoints,
  proximityActionLabel,
} from "@/utils/worldMapProximity"

const PLANES: MapPlane[] = ["SURFACE", "UNDERWORLD", "HEAVEN"]

function mapKindColor(kind: WorldMapPoint["kind"]): string {
  if (kind === "scan") return "#22C55E"
  if (kind === "boss") return "#EF4444"
  if (kind === "worldboss") return "#C084FC"
  if (kind === "postoffice") return "#38BDF8"
  if (kind === "boostershop") return "#FBBF24"
  if (kind === "treasure") return "#F59E0B"
  if (kind === "bank") return "#10B981"
  if (kind === "cityhall") return "#F59E0B"
  if (kind === "hotel") return "#38BDF8"
  if (kind === "hangar") return "#A78BFA"
  return "#A78BFA"
}

function postToMapFrame(contentWindow: Window, payload: Record<string, unknown>) {
  contentWindow.postMessage(JSON.stringify(payload), "*")
}

function mapLocationPointsToPayload(
  points: LocationPoint[],
  icons: MapMarkerIconSet
) {
  return points.map((point) => {
    const kind: WorldMapPointKind = point.mapKind ?? "scan"
    return {
      id: point.id,
      title: point.title,
      lat: point.lat,
      lng: point.lng,
      radiusM: point.radiusM,
      isAvailable: point.isAvailable,
      isOwnCity: point.isOwnCity !== false,
      dominantLeague: point.dominantLeague ?? "NEUTRAL",
      kind,
      label: point.mapLabel ?? undefined,
      iconRef: resolveMapMarkerIconRef(
        kind,
        point.id,
        point.dominantLeague,
        point.treasureMode
      ),
      iconUri: resolveMapMarkerIconUri(
        icons,
        kind,
        point.id,
        point.dominantLeague,
        point.treasureMode
      ),
    }
  })
}

function mapOtherPlayersToPayload(
  players: MapPlayerPresence[],
  icons: MapMarkerIconSet
) {
  return expandMapPlayersToTrailMarkers(players).map((marker) => ({
    markerId: marker.markerId,
    userId: marker.userId,
    nickname: marker.nickname,
    lat: marker.lat,
    lng: marker.lng,
    ageSec: marker.ageSec,
    opacity: marker.opacity,
    affiliationElement: marker.affiliationElement ?? null,
    avatarVariant: marker.avatarVariant ?? 1,
    profileFrameKey: marker.profileFrameKey ?? null,
    iconUri: resolvePlayerMapIconUri(
      icons,
      marker.affiliationElement,
      marker.avatarVariant
    ),
  }))
}

export default function GuardiansMapFrame() {
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const locationPointsRef = useRef<LocationPoint[]>([])
  const mapBasesRef = useRef<BaseMapMarker[]>([])
  const [mapReady, setMapReady] = useState(false)
  const [mapPlane, setMapPlane] = useState<MapPlane>("SURFACE")
  const [mapFilters, setMapFilters] = useState<GuardiansMapFilters>(
    DEFAULT_GUARDIANS_MAP_FILTERS
  )
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectedBase, setSelectedBase] = useState<BaseMapMarker | null>(null)
  const [icons, setIcons] = useState<MapMarkerIconSet | null>(null)
  const [gps, setGps] = useState<PlayerGps | null>(null)
  const [otherPlayers, setOtherPlayers] = useState<MapPlayerPresence[]>([])
  const otherPlayersRef = useRef<MapPlayerPresence[]>([])
  const [viewportBounds, setViewportBounds] = useState<{
    minLat: number
    minLng: number
    maxLat: number
    maxLng: number
  } | null>(null)
  const didAutoFitRef = useRef(false)

  const isBasePlane = mapPlane === "UNDERWORLD" || mapPlane === "HEAVEN"

  const { passport } = useGuardiansPassport()
  const worldQuery = useGuardiansWorldMapPoints()

  const mineQuery = useQuery({
    queryKey: ["base-mine"],
    queryFn: getMyBase,
  })

  const filteredWorldPoints = useMemo(() => {
    const world = worldQuery.data?.points ?? []
    return filterWorldMapPoints(world, mapFilters)
  }, [worldQuery.data?.points, mapFilters])

  const mapLocationPoints = useMemo(() => {
    return worldMapPointsToLocationPoints(filteredWorldPoints)
  }, [filteredWorldPoints])

  const nearbyActionPoints = useMemo(
    () =>
      listActionableWorldMapPoints(
        filteredWorldPoints,
        gps,
        worldQuery.data?.foreignScans ?? null
      ),
    [filteredWorldPoints, gps, worldQuery.data?.foreignScans]
  )

  const boundsPad = 0.04
  const gpsBounds = gps
    ? {
        minLat: gps.lat - boundsPad,
        minLng: gps.lng - boundsPad,
        maxLat: gps.lat + boundsPad,
        maxLng: gps.lng + boundsPad,
      }
    : null
  const bounds = viewportBounds ?? gpsBounds

  otherPlayersRef.current = otherPlayers
  locationPointsRef.current = mapLocationPoints

  const visiblePlayers = useMemo(() => {
    if (isBasePlane || !mapFilters.players) return []
    return otherPlayers
  }, [isBasePlane, mapFilters.players, otherPlayers])

  const baseMapQuery = useQuery({
    queryKey: [
      "base-map-markers",
      mapPlane,
      bounds?.minLat,
      bounds?.minLng,
      bounds?.maxLat,
      bounds?.maxLng,
    ],
    queryFn: () =>
      getBaseMap({
        plane: mapPlane,
        bounds: bounds!,
      }),
    enabled: Boolean(bounds && mapReady && isBasePlane),
    staleTime: 45_000,
  })

  const mapBases = baseMapQuery.data?.bases ?? []
  mapBasesRef.current = mapBases

  const hexesQuery = useQuery({
    queryKey: [
      "base-hexes",
      mapPlane,
      bounds?.minLat,
      bounds?.minLng,
      bounds?.maxLat,
      bounds?.maxLng,
    ],
    queryFn: () =>
      getBaseHexes({
        plane: mapPlane,
        minLat: bounds!.minLat,
        minLng: bounds!.minLng,
        maxLat: bounds!.maxLat,
        maxLng: bounds!.maxLng,
      }),
    enabled: Boolean(bounds && mapReady && isBasePlane),
    staleTime: 45_000,
  })

  const airshipQuery = useQuery({
    queryKey: ["airship-map-missions"],
    queryFn: getAirshipActiveMissions,
    enabled: mapReady && mapPlane === "SURFACE",
    refetchInterval: 30_000,
  })

  const airshipMissionsPayload = useMemo(() => {
    if (mapPlane !== "SURFACE") return []
    return (airshipQuery.data ?? []).map((mission) => {
      const pos = lerpAirshipMissionPos(mission)
      return {
        id: mission.id,
        status: mission.status ?? null,
        ownerNickname: mission.scouted ? mission.ownerNickname : null,
        ownerLeague: mission.scouted ? mission.ownerLeague : null,
        isOwn: mission.isOwn,
        scouted: mission.scouted,
        route: mission.route,
        currentPos: pos,
        progress: mission.progress,
        departedAt: mission.departedAt ?? null,
        arriveAt: mission.arriveAt ?? null,
        returnAt: mission.returnAt ?? null,
      }
    })
  }, [airshipQuery.data, mapPlane])

  const activeFilterCount = useMemo(
    () => countActiveMapFilters(mapFilters),
    [mapFilters]
  )

  const openBaseById = useCallback(
    (baseId: string) => {
      const ownId = mineQuery.data?.base?.id
      if (ownId && ownId === baseId) {
        navigate("/play/base-map")
        return
      }
      const marker = mapBasesRef.current.find((b) => b.id === baseId) ?? null
      if (marker) setSelectedBase(marker)
    },
    [mineQuery.data?.base?.id, navigate]
  )

  useEffect(() => {
    void loadMapMarkerIcons().then(setIcons)
  }, [])

  useEffect(() => {
    void getPlayerGpsOnce().then(setGps)
    return watchPlayerGps(setGps)
  }, [])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return
      try {
        const raw = event.data
        const data = (
          typeof raw === "string" ? JSON.parse(raw) : raw
        ) as Record<string, unknown>
        if (data?.type === "mapReady") setMapReady(true)
        if (data?.type === "pointPress" && typeof data.pointId === "string") {
          const pointId = data.pointId
          const point = locationPointsRef.current.find((p) => p.id === pointId)
          navigate(playRouteForMapPoint(point, pointId))
        }
        if (data?.type === "basePress" && typeof data.baseId === "string") {
          openBaseById(data.baseId)
        }
        if (data?.type === "hexPress") {
          const baseId =
            typeof data.baseId === "string" ? data.baseId : null
          if (baseId) {
            openBaseById(baseId)
          }
        }
        if (
          data?.type === "airshipMissionPress" &&
          typeof data.missionId === "string"
        ) {
          navigate(
            `/play/airship?missionId=${encodeURIComponent(data.missionId)}`
          )
        }
        if (data?.type === "playerPress" && typeof data.userId === "number") {
          const player = otherPlayersRef.current.find(
            (p) => p.userId === data.userId
          )
          if (player?.publicGameCode) {
            navigate(
              `/play/player/${encodeURIComponent(player.publicGameCode)}`
            )
          } else {
            navigate("/play/battle-lobby")
          }
        }
        if (data?.type === "viewportChanged") {
          const minLat = Number(data.minLat)
          const minLng = Number(data.minLng)
          const maxLat = Number(data.maxLat)
          const maxLng = Number(data.maxLng)
          if (
            Number.isFinite(minLat) &&
            Number.isFinite(minLng) &&
            Number.isFinite(maxLat) &&
            Number.isFinite(maxLng)
          ) {
            setViewportBounds({ minLat, minLng, maxLat, maxLng })
          }
        }
      } catch {
        // ignore
      }
    }
    window.addEventListener("message", onMessage)
    return () => window.removeEventListener("message", onMessage)
  }, [navigate, openBaseById])

  useEffect(() => {
    void connectLocationSocket().catch(() => {})

    const offUpdated = onLocationEvent("location-map-updated", (payload) => {
      const player = normalizeLocationMapUpdatedPlayer(payload)
      if (!player) return
      setOtherPlayers((prev) => {
        const idx = prev.findIndex((p) => p.userId === player.userId)
        if (idx >= 0) {
          const next = [...prev]
          next[idx] = player
          return next
        }
        return [...prev, player]
      })
    })

    const offRemoved = onLocationEvent("location-map-removed", (payload) => {
      setOtherPlayers((prev) =>
        prev.filter((p) => p.userId !== payload.userId)
      )
    })

    return () => {
      offUpdated()
      offRemoved()
      void emitLocationEventWhenReady(null, "location-leave")
      disconnectLocationSocket()
    }
  }, [])

  useEffect(() => {
    if (!gps) return
    void emitLocationEventWhenReady(null, "location-heartbeat", {
      playerLat: gps.lat,
      playerLng: gps.lng,
      accuracyM: gps.accuracy,
    })
  }, [gps?.lat, gps?.lng, gps?.accuracy])

  useEffect(() => {
    if (!gps || !mapReady) return
    const t = window.setInterval(() => {
      void emitLocationEventWhenReady(null, "location-heartbeat", {
        playerLat: gps.lat,
        playerLng: gps.lng,
        accuracyM: gps.accuracy,
      })
    }, 25_000)
    return () => window.clearInterval(t)
  }, [gps, mapReady])

  const mapDataReady =
    worldQuery.isFetched || worldQuery.isError || worldQuery.isSuccess
  const cityPolygonsPayload = useMemo(() => {
    const cities = worldQuery.data?.cities ?? []
    const league = worldQuery.data?.ownCityDominantLeague ?? null
    return citiesToMapPolygons(cities, league)
  }, [worldQuery.data?.cities, worldQuery.data?.ownCityDominantLeague])

  const pushMapUpdate = useCallback(() => {
    const win = iframeRef.current?.contentWindow
    if (!win || !mapReady || !icons) return

    const points = isBasePlane ? [] : mapLocationPoints
    const cityPolygons = cityPolygonsPayload.map((c) => ({
      cityId: c.cityId,
      title: c.title,
      polygon: c.polygon,
      isOwnCity: c.isOwnCity,
      dominantLeague: c.dominantLeague ?? undefined,
    }))

    const shouldAutoFit = mapDataReady && !didAutoFitRef.current
    if (shouldAutoFit) didAutoFitRef.current = true

    const iconsPayload = buildMapIconsPayload(
      icons,
      passport?.affiliationElement,
      passport?.avatarVariant
    )

    postToMapFrame(win, { type: "setMarkerIcons", icons: iconsPayload })
    postToMapFrame(win, {
      type: "update",
      icons: iconsPayload,
      playerNickname: passport?.nickname?.trim() || undefined,
      playerProfileFrameKey: passport?.profileFrameKey ?? null,
      position: gps
        ? { latitude: gps.lat, longitude: gps.lng }
        : undefined,
      cityPolygons,
      points: mapLocationPointsToPayload(points, icons),
      otherPlayers: mapOtherPlayersToPayload(visiblePlayers, icons),
      airshipMissions: airshipMissionsPayload,
      autoFit: shouldAutoFit,
    })
  }, [
    mapReady,
    icons,
    mapLocationPoints,
    cityPolygonsPayload,
    mapDataReady,
    gps,
    passport?.affiliationElement,
    passport?.avatarVariant,
    passport?.nickname,
    passport?.profileFrameKey,
    visiblePlayers,
    airshipMissionsPayload,
    isBasePlane,
  ])

  useEffect(() => {
    pushMapUpdate()
  }, [pushMapUpdate])

  useEffect(() => {
    const win = iframeRef.current?.contentWindow
    if (!win || !mapReady) return
    postToMapFrame(win, { type: "setMapPlane", plane: mapPlane })
  }, [mapPlane, mapReady])

  useEffect(() => {
    const win = iframeRef.current?.contentWindow
    if (!win || !mapReady) return
    const hexes = isBasePlane ? (hexesQuery.data?.hexes ?? []) : []
    postToMapFrame(win, {
      type: "setHexes",
      hexes: hexes.map((h) => ({
        q: h.q,
        r: h.r,
        center: h.center,
        polygon: h.polygon,
        occupied: h.occupied,
        baseId: h.baseId,
        isOwn: h.isOwn,
      })),
    })
  }, [hexesQuery.data, mapReady, isBasePlane])

  useEffect(() => {
    const win = iframeRef.current?.contentWindow
    if (!win || !mapReady) return
    postToMapFrame(win, {
      type: "setAirshipMissions",
      missions: airshipMissionsPayload,
    })
  }, [airshipMissionsPayload, mapReady])

  useEffect(() => {
    const win = iframeRef.current?.contentWindow
    if (!win || !mapReady) return
    const bases = isBasePlane ? mapBases : []
    postToMapFrame(win, {
      type: "setBaseMarkers",
      bases: bases.map((base) => ({
        id: base.id,
        nickname: base.nickname,
        league: base.league,
        center: base.center,
        coreLevel: base.coreLevel,
        stockFullness: base.stockFullness,
        hasShield: base.hasShield,
      })),
    })
  }, [mapBases, mapReady, isBasePlane])

  return (
    <div className="space-y-2">
      {worldQuery.isLoading ? (
        <p className="px-2 text-xs text-guardians-muted">Загрузка точек карты…</p>
      ) : worldQuery.isError ? (
        <button
          type="button"
          className="mx-2 rounded-lg border border-red-400/40 px-3 py-2 text-xs text-red-300"
          onClick={() => void worldQuery.refetch()}
        >
          Не удалось загрузить точки — нажмите для повтора
        </button>
      ) : null}
      <div className="flex flex-wrap items-center gap-1 px-1">
        {PLANES.map((plane) => (
          <button
            key={plane}
            type="button"
            onClick={() => setMapPlane(plane)}
            className={clsx(
              "rounded-lg px-2 py-1 text-[10px] uppercase tracking-wide",
              mapPlane === plane
                ? "bg-guardians-accent text-guardians-bg"
                : "border border-white/15 text-guardians-muted"
            )}
          >
            {plane === "SURFACE"
              ? "Поверхность"
              : plane === "UNDERWORLD"
                ? "Подземелье"
                : "Небо"}
          </button>
        ))}
        <MapFiltersToggleButton
          activeCount={activeFilterCount}
          onClick={() => setFiltersOpen(true)}
        />
      </div>
      <div className="relative">
        <iframe
          ref={iframeRef}
          title="Карта хранителей"
          className="h-[calc(100dvh-9rem)] w-full rounded-xl border border-white/10 bg-black/40"
          srcDoc={LOCATION_SCAN_MAP_HTML}
          sandbox="allow-scripts allow-same-origin"
        />
        {!isBasePlane && nearbyActionPoints.length > 0 ? (
          <div
            className={clsx(
              "pointer-events-none absolute inset-x-0 bottom-3 z-10 flex px-3",
              nearbyActionPoints.length === 1 ? "justify-center" : "justify-start"
            )}
          >
            <div
              className={clsx(
                "pointer-events-auto flex gap-2 overflow-x-auto pb-1",
                nearbyActionPoints.length === 1 ? "w-full max-w-md" : "max-w-full"
              )}
            >
              {nearbyActionPoints.map((point) => {
                const iconUri =
                  icons &&
                  resolveMapMarkerIconUri(
                    icons,
                    point.kind,
                    point.id,
                    point.dominantLeague,
                    point.treasure?.mode
                  )
                const isSingle = nearbyActionPoints.length === 1
                return (
                  <button
                    key={point.id}
                    type="button"
                    className={clsx(
                      "flex shrink-0 items-center gap-2 rounded-2xl border border-white/20 px-3 py-2.5 text-left shadow-lg",
                      isSingle ? "min-h-[4rem] w-full" : "min-w-[8.5rem] max-w-[10.5rem] flex-col py-2"
                    )}
                    style={{ backgroundColor: mapKindColor(point.kind) }}
                    onClick={() => {
                      const loc = locationPointsRef.current.find(
                        (p) => p.id === point.id
                      )
                      navigate(playRouteForMapPoint(loc, point.id))
                    }}
                  >
                    {iconUri ? (
                      <img
                        src={iconUri}
                        alt=""
                        className={clsx(
                          "object-contain",
                          isSingle ? "h-8 w-8" : "h-7 w-7"
                        )}
                      />
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-black text-[#0a0a14]">
                        {proximityActionLabel(point.kind)}
                      </span>
                      <span
                        className={clsx(
                          "block font-bold text-[#0a0a14]/70",
                          isSingle ? "text-xs" : "text-[11px] leading-tight"
                        )}
                      >
                        {point.title}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>

      <GuardiansMapFiltersPanel
        open={filtersOpen}
        filters={mapFilters}
        onChange={setMapFilters}
        onClose={() => setFiltersOpen(false)}
      />

      <MapBaseDetailSheet
        marker={selectedBase}
        onClose={() => setSelectedBase(null)}
        onOpenOwnBase={() => navigate("/play/base-map")}
      />
    </div>
  )
}
